import React, { useCallback, useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs
} from "firebase/firestore";
import { db } from "../firebase/config";
import "./StatisticsTab.css";
import Emoji from "./Emoji";

function StatisticsTab() {
  const [children, setChildren] = useState([]);
  const [taskStats, setTaskStats] = useState({});
  const [purchaseStats, setPurchaseStats] = useState({});
  const [topTasks, setTopTasks] = useState([]);
  const [topPurchases, setTopPurchases] = useState([]);
  const [startDate, setStartDate] = useState(getNDaysAgo(6));
  const [endDate, setEndDate] = useState(getToday());

  const getTimestampRange = useCallback(() => {
    const from = new Date(startDate);
    from.setHours(0, 0, 0, 0);

    const to = new Date(endDate);
    to.setHours(23, 59, 59, 999);

    return { from, to };
  }, [endDate, startDate]);

  const loadChildren = useCallback(async () => {
    const q = query(collection(db, "users"), where("type", "==", "child"));
    const snap = await getDocs(q);
    const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setChildren(list);
  }, []);

  const loadStats = useCallback(async () => {
    const { from, to } = getTimestampRange();

    // TASK STATS
    const taskQ = query(collection(db, "taskHistory"), where("status", "==", "verified"));
    const taskSnap = await getDocs(taskQ);
    const verifiedTasks = taskSnap.docs
      .map(doc => doc.data())
      .filter(task =>
        task.completedAt?.toDate &&
        task.completedAt.toDate() >= from &&
        task.completedAt.toDate() <= to
      );

    const taskPerChild = {};
    const taskCountByTitle = {};

    verifiedTasks.forEach(t => {
      taskPerChild[t.child] = (taskPerChild[t.child] || 0) + (t.points || 0);
      taskCountByTitle[t.title] = (taskCountByTitle[t.title] || 0) + 1;
    });

    // PURCHASE STATS
    const purchaseSnap = await getDocs(collection(db, "purchaseHistory"));
    const purchases = purchaseSnap.docs
      .map(doc => doc.data())
      .filter(p =>
        p.purchasedAt?.toDate &&
        p.purchasedAt.toDate() >= from &&
        p.purchasedAt.toDate() <= to
      );

    const purchasePerChild = {};
    const purchaseCountByTitle = {};

    purchases.forEach(p => {
      purchasePerChild[p.child] = (purchasePerChild[p.child] || 0) + Number(p.price || 0);
      purchaseCountByTitle[p.title] = (purchaseCountByTitle[p.title] || 0) + 1;
    });

    setTaskStats(taskPerChild);
    setPurchaseStats(purchasePerChild);

    const sortedTasks = Object.entries(taskCountByTitle)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    setTopTasks(sortedTasks);

    const sortedPurchases = Object.entries(purchaseCountByTitle)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    setTopPurchases(sortedPurchases);
  }, [getTimestampRange]);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  useEffect(() => {
    if (children.length > 0) {
      loadStats();
    }
  }, [children, loadStats]);

  return (
    <div className="statistics-tab">
      <div className="date-picker-row">
        <label>Період:</label>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        <span>—</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
      </div>

      <h3>Загальна статистика</h3>
      <table className="stats-table">
        <thead>
          <tr>
            <th>Ім'я</th>
            <th>Отримано</th>
            <th>Витрачено</th>
            <th>Підсумок</th>
          </tr>
        </thead>
        <tbody>
  {children.map(child => {
    const earned = taskStats[child.id] || 0;
    const spent = purchaseStats[child.id] || 0;
    return (
      <tr key={child.id}>
        <td data-label="Дитина">{child.name}</td>
        <td data-label="Отримано"><Emoji label="Кубок" symbol="🏆" /> {earned}</td>
        <td data-label="Витрачено"><Emoji label="Покупки" symbol="🛍️" /> {spent}</td>
        <td data-label="Баланс"><Emoji label="Гроші" symbol="💰" /> {earned - spent}</td>
      </tr>
    );
  })}
</tbody>


      </table>

      <div className="top-lists">
        <div className="top-block">
          <h4><Emoji label="Кубок" symbol="🏆" /> Топ-5 виконаних завдань</h4>
          <ul>
            {topTasks.map(([title, count]) => (
              <li key={title}>{title} — {count} разів</li>
            ))}
          </ul>
        </div>

        <div className="top-block">
          <h4><Emoji label="Покупки" symbol="🛍️" /> Топ-5 покупок</h4>
          <ul>
            {topPurchases.map(([title, count]) => (
              <li key={title}>{title} — {count} разів</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

const getToday = () => new Date().toISOString().slice(0, 10);
const getNDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default StatisticsTab;
